<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useUI } from '@/composables/useUI'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoTimeStamp from '@/components/base/campos/CampoTimeStamp.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { listaPaises } from '@/utils/listaPaises'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'
import { PARCEIRO_TIPO } from '@/constants/PARCEIRO_TIPO'
import { PARCEIRO_ESFERA_GOV } from '@/constants/PARCEIRO_ESFERA_GOV'
import { PARCEIRO_VND_PAPEL_COM } from '@/constants/PARCEIRO_VND_PAPEL_COM'
import { criarConfigBuscaEnderecoParceiro } from '@/search-definitions/parceiroEnderecoSearch'
import { criarConfigBuscaParceiroGrupo } from '@/search-definitions/parceiroGrupoSearch'
import { criarConfigBuscaParceiroInstituicaoFinanceira } from '@/search-definitions/parceiroInstituicaoFinanceiraSearch'
import { criarConfigBuscaParceiroVendedor } from '@/search-definitions/parceiroVendedorSearch'
import { criarConfigBuscaUsuario } from '@/search-definitions/usuarioSearch'
import { criarConfigBuscaPrecoComercial } from '@/search-definitions/precoComercialSearch'
import { formatarTimestampBR } from '@/utils/useDataBR'

const props = defineProps({
  itemParaEditar: { type: Object, default: null }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification', 'changesDetected'])

const authStore = useAuthStore()
const { obterClasseStatusGhost } = useUI()

// --- Configuração da API de Geolocalização ---
const OPENCAGE_API_KEY = 'a0bf8e0cec934a608fc71997d036a80e'

// --- Controle de Permissões ---
const podeEditar = computed(() => {
  return authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('PARCEIRO_COMPLETO')) || true // Fallback mantido para não quebrar fluxos legados
})

// --- Mapeamento de Campos ---
const mapaCampos = {
  lstTipoPessoa: 'Tipo Pessoa',
  documento: 'Documento (CPF / CNPJ / Tax ID)',
  lstTipoDocumento: 'Tipo de Documento',
  nomeFantasia: 'Nome Fantasia',
  lstStatus: 'Status',
  razaoSocial: 'Razão Social',
  lstRegimeTributario: 'Regime Tributário',
  lstSegmento: 'Segmento',
  lstRamoAtividade: 'Ramo de Atividade',
  lstCategoria: 'Categoria do Fornecedor',
  inscricaoEstadual: 'Inscrição Estadual',
  inscricaoMunicipal: 'Inscrição Municipal',
  codigoIbge: 'Código IBGE do Município', // GUARDIÃO: Adicionado para validação Híbrida
  codigoIbgeGoverno: 'Código IBGE do Município', // GUARDIÃO: ID único para o frame Governo (evita conflito de ID duplicado)
  lstEsferaGovernamental: 'Esfera Governamental',
  apelido: 'Apelido (Interno)',
  emailPrincipal: 'E-mail Principal',
  telFixo: 'Telefone Fixo',
  telCelular: 'Celular / WhatsApp',
  site: 'Website',
  suframa: 'Código SUFRAMA',
  rntrc: 'RNTRC',
  codigoAntt: 'Código ANTT',
  codigoCompe: 'Código COMPE',
  swiftBic: 'SWIFT / BIC',
  observacoes: 'Observações'
}

const { item, isLoading: isSaving, isEditing, entityInfo, submitForm, handleCancel, initializeForm } = useFormulario(props, emit, { mapaCampos })

// ============================================================
// FUNÇÃO: Salvar Alterações (via @submit-continuar)
// ============================================================
const salvarAlteracoesParceiro = async () => {
  if (isSaving.value || !isParceiroSaved.value) return

  const payload = {
    ...item.value,
    isGrupoEmpresa: !!item.value.isGrupoEmpresa,
    isCliente: !!item.value.isCliente,
    isFornecedor: !!item.value.isFornecedor,
    isTransportadora: !!item.value.isTransportadora,
    isInstFinanceira: !!item.value.isInstFinanceira,
    isOrgaoGoverno: !!item.value.isOrgaoGoverno,
    isVendedor: !!item.value.isVendedor,
    enderecos: Array.isArray(item.value.enderecos) ? item.value.enderecos : [],
    contasBancarias: Array.isArray(item.value.contasBancarias) ? item.value.contasBancarias : []
  }

  // Adiciona atributosCliente no payload
  const atributosClientePayload = Object.keys(respostasAtributosCliente)
    .filter(prefixo => respostasAtributosCliente[prefixo])
    .map(prefixo => ({
      prefixoItemLista: prefixo,
      lstValorAtributo: respostasAtributosCliente[prefixo]
    }))
  payload.atributosCliente = atributosClientePayload

  // Remove modaisTransportadora do payload
  delete payload.modaisTransportadora

  // Sanitiza codigoIbge
  if (!item.value.isFornecedor && !payload.codigoIbge) {
    payload.codigoIbge = null
  }

  try {
    const res = await api.put(`/Parceiros/${item.value.idParceiro}`, payload)
    if (res?.data) {
      initializeForm(res.data)
    }
    // Salva dados do vendedor se necessário
    await salvarVendedorSeNecessario()
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar alterações.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Estado de "Salvamento Intermediário" (libera coleções após salvar dados básicos) ---
const isParceiroSaved = ref(isEditing.value)
const isSavingParceiroLocal = ref(false)
const isColecoesBlocked = computed(() => !isParceiroSaved.value)

// --- Estado do Modal de Auditoria ---
const mostrarAuditoria = ref(false)
const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

// --- Configurações de Busca Computadas para injetar empresaAtiva dinamicamente ---
const confBuscaGrupo = computed(() => criarConfigBuscaParceiroGrupo(authStore.organizacaoAtiva))

const confBuscaBanco = computed(() => criarConfigBuscaParceiroInstituicaoFinanceira(authStore.organizacaoAtiva))

const confBuscaVendedor = computed(() => {
  return criarConfigBuscaParceiroVendedor(authStore.organizacaoAtiva)
})

const confBuscaGestorComercial = computed(() => {
  return criarConfigBuscaParceiroVendedor(authStore.organizacaoAtiva, PARCEIRO_VND_PAPEL_COM.GestorComercial)
})

const confBuscaUsuario = computed(() => criarConfigBuscaUsuario(authStore.isAdminInterno, authStore.empresaAtiva))

const confBuscaPrecoComercial = computed(() => criarConfigBuscaPrecoComercial())

// --- Configurações de Busca para Endereços do Próprio Parceiro (IdEndereco*) ---
const confBuscaEnderecoParceiro = computed(() => {
  if (!item.value.idParceiro) return {}
  return criarConfigBuscaEnderecoParceiro(item.value.idParceiro)
})

// --- Estado Local ---
const accordionState = reactive({
  basico: true,
  enderecos: false,
  contas: false,
  clausulas: false,
  cliente: false,
  fornecedor: false,
  transportadora: false,
  financeira: false,
  governo: false,
  vendedor: false
})

const listas = reactive({
  tiposPessoa: [],
  status: [],
  regimes: [],
  segmentos: [],
  ramos: [],
  categorias: [],
  modais: [],
  moedas: [],
  tiposPix: [],
  routingTypes: [],
  esferasGov: [],
  tiposDocumento: [],
  lstAplicacaoClausula: [],
  lstVisibilidade: [],
  lstProcessoInterno: []
})

// --- Estado para Atributos do Cliente (Listas Vinculadas) ---
const listasVinculadasCliente = ref([])
const respostasAtributosCliente = reactive({})

async function carregarListasVinculadasCliente () {
  if (!authStore.empresaAtiva) return
  try {
    const res = await api.get(`/Empresas/${authStore.empresaAtiva}`)
    const prefixos = res.data.listasVinculadasClientes || []

    const listas = []
    for (const prefixo of prefixos) {
      try {
        const resLista = await api.get(`/ListasCliente/por-prefixo/${prefixo}`)
        listas.push({
          prefixoItemLista: prefixo,
          nomeLista: resLista.data.nomeLista
        })
      } catch (e) {
        listas.push({ prefixoItemLista: prefixo, nomeLista: `Lista: ${prefixo}` })
      }
    }
    listasVinculadasCliente.value = listas

    // Se estiver editando, popular respostas a partir do item
    if (props.itemParaEditar && item.value.atributosCliente) {
      for (const attr of item.value.atributosCliente) {
        respostasAtributosCliente[attr.prefixoItemLista] = attr.lstValorAtributo
      }
    }
  } catch (e) {
    console.warn('Erro ao carregar listas vinculadas a clientes:', e)
  }
}

const enderecoEmEdicao = ref(null)
const contaEmEdicao = ref(null)

// --- Computeds e Lógica SEFAZ ---
const showModalEndereco = computed({
  get: () => !!enderecoEmEdicao.value,
  set: (val) => { if (!val) enderecoEmEdicao.value = null }
})

const showModalConta = computed({
  get: () => !!contaEmEdicao.value,
  set: (val) => { if (!val) contaEmEdicao.value = null }
})

const isEstrangeiro = computed(() => {
  return item.value.lstTipoPessoa === PARCEIRO_TIPO.Estrangeiro
})

// GUARDIÃO: Extrator de Nível Fiscal para cruzamento de regras
const nivelFiscal = ref(NV_MODULO_FISCAL.Nenhum)

const carregarNivelFiscal = async () => {
  let nivelCodigo = NV_MODULO_FISCAL.Nenhum
  try {
    if (authStore.isAdminInterno) {
      if (authStore.empresaAtiva) {
        const resEmpresa = await api.get(`/Empresas/${authStore.empresaAtiva}`)
        const idPlano = resEmpresa.data.idPlanoVigente || resEmpresa.data.IdPlanoVigente
        if (idPlano) {
          const resPlano = await api.get(`/Planos/${idPlano}`)
          nivelCodigo = resPlano.data.lstNivelModuloFiscal || resPlano.data.LstNivelModuloFiscal || NV_MODULO_FISCAL.Nenhum
        }
      }
    } else {
      nivelCodigo = authStore.lstNivelModuloFiscal || NV_MODULO_FISCAL.Nenhum
    }
  } catch (e) {
    // Silenciado
  }

  nivelFiscal.value = String(nivelCodigo).toUpperCase()

  try {
    if (nivelCodigo !== NV_MODULO_FISCAL.Nenhum) {
      const resLista = await api.get('/ListasSistema/lookup/NV_MODULO_FISCAL')
      const itemLista = resLista.data.find(i =>
        String(i.idListaSistemaItem).toUpperCase() === nivelFiscal.value ||
        String(i.textoLista).toUpperCase() === nivelFiscal.value
      )
      nivelFiscal.value = itemLista ? String(itemLista.textoLista).toUpperCase() : nivelFiscal.value
    }
  } catch (e) {
    // Mantém fallback
  }
}

// GUARDIÃO: Regra do Motor Híbrido - Obrigatoriedade do Código IBGE
const isIbgeObrigatorio = computed(() => {
  // Regra 1: Fornecedor com Nível Fiscal Compliance
  if (item.value.isFornecedor &&
      nivelFiscal.value === NV_MODULO_FISCAL.Compliance &&
      !isEstrangeiro.value) {
    return true
  }

  // Regra 2: Órgão de Governo de esfera Municipal
  if (item.value.isOrgaoGoverno &&
      item.value.lstEsferaGovernamental === PARCEIRO_ESFERA_GOV.Municipal) {
    return true
  }

  return false
})

// --- Inicialização ---
if (!props.itemParaEditar) {
  item.value = {
    idOrganizacao: null,
    lstTipoPessoa: null,
    nomeFantasia: '',
    apelido: '',
    razaoSocial: '',
    documento: '',
    lstTipoDocumento: null,
    inscricaoEstadual: '',
    inscricaoMunicipal: '',
    codigoIbge: '',
    idGrupoEmpresa: null,
    nomeGrupoEmpresa: '',
    isCliente: false,
    isFornecedor: false,
    isTransportadora: false,
    isInstFinanceira: false,
    isGrupoEmpresa: false,
    isOrgaoGoverno: false,
    isVendedor: false,
    lstStatus: null,
    lstRegimeTributario: null,
    lstEsferaGovernamental: null,
    observacoes: '',
    emailPrincipal: '',
    telFixo: '',
    telCelular: '',
    site: '',
    lstSegmento: null,
    lstRamoAtividade: null,
    idVendedor: null,
    lstCategoria: null,
    suframa: '',
    rntrc: '',
    codigoAntt: '',
    codigoCompe: '',
    swiftBic: '',
    enderecos: [],
    contasBancarias: [],
    modaisTransportadora: []
  }
}

// --- Watchers ---
watch(() => authStore.empresaAtiva, () => {
  carregarNivelFiscal()
})

watch(() => item.value.isCliente, (val) => { if (val) accordionState.cliente = true })
watch(() => item.value.isFornecedor, (val) => { if (val) accordionState.fornecedor = true })
watch(() => item.value.isTransportadora, (val) => { if (val) accordionState.transportadora = true })
watch(() => item.value.isInstFinanceira, (val) => { if (val) accordionState.financeira = true })
watch(() => item.value.isOrgaoGoverno, (val) => { if (val) accordionState.governo = true })
watch(() => item.value.isVendedor, (val) => { if (val) accordionState.vendedor = true })

watch(isEstrangeiro, (val) => {
  if (enderecoEmEdicao.value) {
    enderecoEmEdicao.value.pais = val ? '' : 'Brasil'
    enderecoEmEdicao.value.uf = val ? 'EX' : ''
  }

  // GUARDIÃO SEFAZ: Feedback visual imediato da regra dos Estrangeiros
  if (val) {
    item.value.codigoIbge = '9999999'
  } else if (item.value.codigoIbge === '9999999') {
    item.value.codigoIbge = ''
  }
})

watch(() => item.value.isGrupoEmpresa, (newVal) => {
  const isTrue = newVal === true || String(newVal).toLowerCase() === 'true'
  if (item.value.isGrupoEmpresa !== isTrue) {
    item.value.isGrupoEmpresa = isTrue
  }
  if (isTrue) {
    item.value.idGrupoEmpresa = null
  }
})

// --- Utilitários ---
const formatarCep = (e) => {
  if (!enderecoEmEdicao.value) {
    console.warn('[DEBUG formatarCep] enderecoEmEdicao está null — abortando')
    return
  }
  let v = e.target.value.replace(/\D/g, '')
  if (v.length > 5) v = v.replace(/^(\d{5})(\d)/, '$1-$2')
  enderecoEmEdicao.value.cep = v
}

function getStatusAtivoId () {
  const st = listas.status.find(i => i.textoLista.toUpperCase() === 'ATIVO')
  return st ? st.idListaClienteItem : null
}

function getStatusTexto (idStatus) {
  const statusObj = listas.status.find(s => s.idListaClienteItem === idStatus)
  return statusObj ? statusObj.textoLista : idStatus
}

function getMoedaTexto (idMoeda) {
  if (!idMoeda) return '-'
  const moedaObj = listas.moedas.find(m => String(m.idListaClienteItem) === String(idMoeda))
  return moedaObj ? moedaObj.textoLista : idMoeda
}

// --- Carregamento de Listas e Defaults ---
async function carregarListas () {
  const fetchList = async (prefixo, key) => {
    try {
      const res = await api.get(`/ListasCliente/lookup/${prefixo}`)
      listas[key] = res.data
    } catch (error) {
      console.warn(`Falha na lista ${key}:`, error)
    }
  }

  await Promise.allSettled([
    fetchList('PARCEIRO_TIPO', 'tiposPessoa'),
    fetchList('ST', 'status'),
    fetchList('REGIME_TRIBUTARIO', 'regimes'),
    fetchList('MODAL_LOG', 'modais'),
    fetchList('SEGMENTO_COMERCIAL', 'segmentos'),
    fetchList('PARCEIRO_RAMO_ATIVIDADE', 'ramos'),
    fetchList('PCAT', 'categorias'),
    fetchList('MOE', 'moedas'),
    fetchList('PTPIX', 'tiposPix'),
    fetchList('PRT', 'routingTypes'),
    fetchList('PARCEIRO_ESFERA_GOV', 'esferasGov'),
    fetchList('CLAUSULAS_COM_APLIC', 'lstAplicacaoClausula'),
    fetchList('CLAUSULAS_COM_VISIB', 'lstVisibilidade'),
    fetchList('CLAUSULAS_COM_PROCESS', 'lstProcessoInterno')
  ])
}

function definirDefaults () {
  if (props.itemParaEditar) return

  const stAtivo = getStatusAtivoId()
  if (stAtivo) item.value.lstStatus = stAtivo

  const tpJuridica = listas.tiposPessoa.find(i => i.textoLista.toUpperCase() === 'JURIDICA')
  if (tpJuridica) item.value.lstTipoPessoa = tpJuridica.idListaClienteItem
}

onMounted(async () => {
  carregarNivelFiscal()
  await carregarListas()
  definirDefaults()
  await carregarListasVinculadasCliente()
  // Se estiver editando, carrega dados relacionados
  if (props.itemParaEditar) {
    if (item.value.enderecos?.length > 0) {
      normalizarEnderecosPrincipal()
    }
    if (item.value.idParceiro) {
      await carregarClausulasFornecedor()
    }
    if (item.value.isVendedor) {
      await carregarVendedorData()
    }
    if (item.value.isCliente) {
      await carregarPrecosCliente()
    }
  }
})

// --- Lógica: Endereços e APIs Geográficas ---
function iniciarNovoEndereco () {
  if (isSaving.value || isColecoesBlocked.value) return
  enderecoEmEdicao.value = {
    _isNew: true,
    idParceiroEndereco: 0,
    descricao: '',
    pais: isEstrangeiro.value ? '' : 'Brasil',
    codigoPais: isEstrangeiro.value ? '' : '1058',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: isEstrangeiro.value ? 'EX' : '',
    latitude: null,
    longitude: null,
    isPrincipal: false,
    lstStatus: getStatusAtivoId()
  }
}

function editarEndereco (end, idx) {
  if (isSaving.value || isColecoesBlocked.value) return
  enderecoEmEdicao.value = { ...end, _index: idx, _isNew: false }
}

async function salvarEnderecoLocal () {
  if (isColecoesBlocked.value) return
  const end = enderecoEmEdicao.value
  if (!end.descricao || !end.pais || !end.numero || !end.lstStatus || (end.pais === 'Brasil' && (!end.logradouro || !end.cidade || !end.uf))) {
    emit('show-notification', { message: 'Por favor, preencha todos os campos obrigatórios do endereço (marcados com *).', type: 'warning' })
    return
  }

  const { _index, _isNew, ...dados } = enderecoEmEdicao.value

  try {
    if (isParceiroSaved.value && item.value.idParceiro) {
      // Parceiro já salvo → persiste via API individual
      if (_isNew) {
        const res = await api.post(`/Parceiros/${item.value.idParceiro}/Enderecos`, dados)
        // Atualiza com o retorno completo do backend (inclui isPrincipal normalizado)
        const endSalvo = res.data
        item.value.enderecos.push(endSalvo)
        emit('show-notification', { message: `Endereço "${endSalvo.descricao}" adicionado com sucesso.`, type: 'success' })
      } else {
        const res = await api.put(`/Parceiros/${item.value.idParceiro}/Enderecos/${dados.idParceiroEndereco}`, dados)
        // Atualiza com o retorno completo do backend (inclui isPrincipal normalizado)
        item.value.enderecos[_index] = res.data
        emit('show-notification', { message: `Endereço "${res.data.descricao}" atualizado com sucesso.`, type: 'success' })
      }
    } else {
      // Parceiro ainda não salvo → mantém local
      if (!_isNew && typeof _index === 'number') {
        item.value.enderecos[_index] = dados
      } else {
        item.value.enderecos.push(dados)
      }
    }

    normalizarEnderecosPrincipal()
    enderecoEmEdicao.value = null
  } catch (error) {
    // Tenta extrair mensagens de validação do ProblemDetails do ASP.NET
    let errorMsg = 'Erro ao salvar endereço.'
    if (error.response?.data && typeof error.response.data === 'string') {
      // BadRequest("string literal") retorna Content-Type: text/plain
      errorMsg = error.response.data
    } else if (error.response?.data?.errors) {
      // ProblemDetails: errors é um dicionário { campo: [mensagens] }
      const msgs = Object.values(error.response.data.errors).flat()
      if (msgs.length > 0) errorMsg = msgs.join('; ')
    } else if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail
    } else if (error.response?.data?.message) {
      errorMsg = error.response.data.message
    } else if (error.response?.data?.title) {
      errorMsg = error.response.data.title
    }
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

async function buscarCoordenadas () {
  if (!enderecoEmEdicao.value?.logradouro || !enderecoEmEdicao.value?.cidade) {
    console.warn('[buscarCoordenadas] Logradouro ou cidade ausentes — geocoding ignorado.')
    return
  }
  if (!OPENCAGE_API_KEY || OPENCAGE_API_KEY === 'SUA_CHAVE_OPENCAGE_AQUI') {
    console.warn('[buscarCoordenadas] Chave OpenCage não configurada — geocoding ignorado.')
    return
  }

  try {
    let query = `${enderecoEmEdicao.value.logradouro}`
    if (enderecoEmEdicao.value.numero) query += `, ${enderecoEmEdicao.value.numero}`
    query += `, ${enderecoEmEdicao.value.cidade}, ${enderecoEmEdicao.value.uf}`
    if (enderecoEmEdicao.value.pais) query += `, ${enderecoEmEdicao.value.pais}`

    const url = `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(query)}&key=${OPENCAGE_API_KEY}`
    const res = await fetch(url)

    if (!enderecoEmEdicao.value) return

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
    const data = await res.json()

    if (!enderecoEmEdicao.value) return

    if (data && data.results && data.results.length > 0) {
      enderecoEmEdicao.value.latitude = data.results[0].geometry.lat
      enderecoEmEdicao.value.longitude = data.results[0].geometry.lng
    }
  } catch (error) {
    console.warn('Erro ao buscar coordenadas:', error)
  }
}

async function buscarCep () {
  const cep = enderecoEmEdicao.value?.cep?.replace(/\D/g, '')
  if (cep && cep.length === 8) {
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`)

      if (!enderecoEmEdicao.value) return

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      const data = await res.json()

      if (!enderecoEmEdicao.value) return

      if (!data.erro) {
        enderecoEmEdicao.value.logradouro = data.logradouro
        enderecoEmEdicao.value.bairro = data.bairro
        enderecoEmEdicao.value.cidade = data.localidade
        enderecoEmEdicao.value.uf = data.uf

        buscarCoordenadas()

        setTimeout(() => {
          const elNum = document.getElementById('numero')
          if (elNum) elNum.focus()
        }, 100)
      } else {
        emit('show-notification', { message: 'CEP não encontrado na base dos Correios.', type: 'warning' })
      }
    } catch (error) {
      console.warn('Erro ao buscar CEP', error)
      emit('show-notification', { message: 'Falha de comunicação ao buscar o CEP.', type: 'error' })
    }
  } else if (cep && cep.length > 0 && cep.length < 8) {
    emit('show-notification', { message: 'CEP inválido. Digite os 8 números.', type: 'warning' })
  }
}

// --- Lógica: Contas ---
function iniciarNovaConta () {
  if (isSaving.value || isColecoesBlocked.value) return
  const moedaBrl = listas.moedas.find(m => m.textoLista === 'BRL')?.idListaClienteItem
  contaEmEdicao.value = {
    _isNew: true,
    idParceiroConta: 0,
    descricao: '',
    idBanco: null,
    nomeBanco: '',
    agencia: '',
    numeroConta: '',
    lstMoeda: moedaBrl || null,
    lstTipoChavePix: null,
    chavePix: '',
    iban: '',
    lstRoutingNumberType: null,
    routingNumber: '',
    lstStatus: getStatusAtivoId(),
    isPadraoNaMoeda: false
  }
}

function editarConta (cta, idx) {
  if (isSaving.value || isColecoesBlocked.value) return
  contaEmEdicao.value = { ...cta, _index: idx, _isNew: false }
}

async function salvarContaLocal () {
  if (isColecoesBlocked.value) return
  const cta = contaEmEdicao.value
  if (!cta.idBanco || !cta.numeroConta || !cta.lstMoeda || !cta.lstStatus) {
    emit('show-notification', { message: 'Por favor, preencha todos os campos obrigatórios da conta bancária (marcados com *).', type: 'warning' })
    return
  }

  const { _index, _isNew, ...dados } = contaEmEdicao.value

  try {
    if (isParceiroSaved.value && item.value.idParceiro) {
      // Parceiro já salvo → persiste via API individual
      if (_isNew) {
        const res = await api.post(`/Parceiros/${item.value.idParceiro}/ContasBancarias`, dados)
        dados.idParceiroConta = res.data.idParceiroConta
        item.value.contasBancarias.push(dados)
        emit('show-notification', { message: `Conta "${dados.descricao || dados.numeroConta}" adicionada com sucesso.`, type: 'success' })
      } else {
        await api.put(`/Parceiros/${item.value.idParceiro}/ContasBancarias/${dados.idParceiroConta}`, dados)
        item.value.contasBancarias[_index] = dados
        emit('show-notification', { message: `Conta "${dados.descricao || dados.numeroConta}" atualizada com sucesso.`, type: 'success' })
      }
    } else {
      // Parceiro ainda não salvo → mantém local
      if (!_isNew && typeof _index === 'number') {
        item.value.contasBancarias[_index] = dados
      } else {
        item.value.contasBancarias.push(dados)
      }
    }

    // Normaliza: apenas uma conta padrão por moeda
    normalizarContasPadraoNaMoeda()
    contaEmEdicao.value = null
  } catch (error) {
    let errorMsg = 'Erro ao salvar conta bancária.'
    if (error.response?.data && typeof error.response.data === 'string') {
      errorMsg = error.response.data
    } else if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail
    } else if (error.response?.data?.message) {
      errorMsg = error.response.data.message
    } else if (error.response?.data?.title) {
      errorMsg = error.response.data.title
    }
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

function onBancoSelecionado (bancoRow) {
  if (bancoRow) {
    contaEmEdicao.value.nomeBanco = bancoRow.nomeFantasia
  }
}

// --- Salvamento Intermediário (Salvar Parceiro e Liberar Coleções) ---
async function salvarParceiroLocalmente () {
  if (isSavingParceiroLocal.value) return

  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  if (!item.value.nomeFantasia || !item.value.documento || !item.value.lstTipoPessoa || !item.value.lstStatus) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios dos Dados Básicos.', type: 'warning' })
    return
  }

  // GUARDIÃO: Órgão de Governo Municipal exige Código IBGE
  if (item.value.isOrgaoGoverno &&
      item.value.lstEsferaGovernamental === PARCEIRO_ESFERA_GOV.Municipal &&
      !item.value.codigoIbge) {
    emit('show-notification', { message: 'O Código IBGE é obrigatório para Órgãos de Governo de esfera Municipal.', type: 'warning' })
    return
  }

  isSavingParceiroLocal.value = true

  const payload = {
    ...item.value,
    enderecos: Array.isArray(item.value.enderecos) ? [...item.value.enderecos] : [],
    contasBancarias: Array.isArray(item.value.contasBancarias) ? [...item.value.contasBancarias] : [],
    modaisTransportadora: Array.isArray(item.value.modaisTransportadora) ? [...item.value.modaisTransportadora] : [],
    isVendedor: !!item.value.isVendedor
  }

  // GUARDIÃO: Sanitiza codigoIbge quando não for Fornecedor,
  // para evitar validação de "exatamente 7 dígitos" do backend em strings vazias
  if (!item.value.isFornecedor && !payload.codigoIbge) {
    payload.codigoIbge = null
  }

  try {
    const response = await api.post('/Parceiros', payload)
    console.log('[DEBUG salvarParceiroLocalmente] response.data:', JSON.stringify(response.data, null, 2))
    console.log('[DEBUG salvarParceiroLocalmente] response.data.enderecos:', response.data.enderecos)
    console.log('[DEBUG salvarParceiroLocalmente] response.data.contasBancarias:', response.data.contasBancarias)
    console.log('[DEBUG salvarParceiroLocalmente] response.data.modaisTransportadora:', response.data.modaisTransportadora)
    item.value = response.data

    console.log('[DEBUG salvarParceiroLocalmente] item.value.enderecos after assign:', item.value.enderecos)
    console.log('[DEBUG salvarParceiroLocalmente] item.value.contasBancarias after assign:', item.value.contasBancarias)

    // Normaliza coleções que o backend pode não retornar
    if (!Array.isArray(item.value.enderecos)) item.value.enderecos = []
    if (!Array.isArray(item.value.contasBancarias)) item.value.contasBancarias = []
    if (!Array.isArray(item.value.modaisTransportadora)) item.value.modaisTransportadora = []

    isParceiroSaved.value = true
    isEditing.value = true
    entityInfo.value = `Editando ID: ${item.value.idParceiro}`

    emit('show-notification', { message: 'Parceiro salvo. Agora pode adicionar endereços e contas.', type: 'success' })

    // Salva dados do vendedor se necessário
    await salvarVendedorSeNecessario()

    accordionState.enderecos = true
  } catch (error) {
    let errorMsg = 'Erro ao salvar o parceiro.'
    if (error.response?.data && typeof error.response.data === 'string') {
      errorMsg = error.response.data
    } else if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail
    } else if (error.response?.data?.message) {
      errorMsg = error.response.data.message
    } else if (error.response?.data?.title) {
      errorMsg = error.response.data.title
    }
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isSavingParceiroLocal.value = false
  }
}

// --- Normalização de Endereço Principal (réplica do backend) ---
function normalizarEnderecosPrincipal () {
  const enderecos = item.value.enderecos
  if (!enderecos || enderecos.length === 0) return

  const principal = enderecos.find(e => e.isPrincipal === true)
  if (!principal) {
    enderecos[0].isPrincipal = true
  } else {
    enderecos.forEach(end => {
      if (end !== principal) end.isPrincipal = false
    })
  }
}

// --- Normalização de Conta Padrão na Moeda (apenas uma por moeda/parceiro) ---
function normalizarContasPadraoNaMoeda () {
  const contas = item.value.contasBancarias
  if (!contas || contas.length === 0) return

  // Agrupa contas por moeda
  const grupos = {}
  contas.forEach((cta, idx) => {
    const moeda = cta.lstMoeda
    if (!moeda) return
    if (!grupos[moeda]) grupos[moeda] = []
    grupos[moeda].push({ conta: cta, index: idx })
  })

  // Para cada moeda, mantém apenas a primeira com isPadraoNaMoeda=true, desmarca as demais
  Object.values(grupos).forEach(grupo => {
    const marcadas = grupo.filter(g => g.conta.isPadraoNaMoeda === true)
    if (marcadas.length > 1) {
      // Mantém a primeira marcada, desmarca as outras
      marcadas.slice(1).forEach(g => {
        g.conta.isPadraoNaMoeda = false
      })
    }
  })
}

// --- Submissão ---
async function salvarParceiro () {
  if (item.value.isFornecedor && item.value.contasBancarias.length === 0) {
    emit('show-notification', { message: 'Fornecedores devem ter ao menos uma conta bancária.', type: 'warning' })
    return
  }

  // GUARDIÃO: Salva os modais selecionados ANTES do submitForm,
  // pois submitForm substitui item.value pela resposta do backend (useFormulario.js:177)
  const modaisSelecionados = Array.isArray(item.value.modaisTransportadora) ? [...item.value.modaisTransportadora] : []

  const payload = {
    ...item.value,
    isGrupoEmpresa: !!item.value.isGrupoEmpresa,
    isCliente: !!item.value.isCliente,
    isFornecedor: !!item.value.isFornecedor,
    isTransportadora: !!item.value.isTransportadora,
    isInstFinanceira: !!item.value.isInstFinanceira,
    isOrgaoGoverno: !!item.value.isOrgaoGoverno,
    isVendedor: !!item.value.isVendedor,
    enderecos: Array.isArray(item.value.enderecos) ? item.value.enderecos : [],
    contasBancarias: Array.isArray(item.value.contasBancarias) ? item.value.contasBancarias : []
  }

  // Adiciona atributosCliente no payload
  const atributosClientePayload = Object.keys(respostasAtributosCliente)
    .filter(prefixo => respostasAtributosCliente[prefixo])
    .map(prefixo => ({
      prefixoItemLista: prefixo,
      lstValorAtributo: respostasAtributosCliente[prefixo]
    }))
  payload.atributosCliente = atributosClientePayload

  // Remove modaisTransportadora do payload — o backend PUT /Parceiros não gerencia esta coleção
  delete payload.modaisTransportadora

  // GUARDIÃO: Sanitiza codigoIbge quando não for Fornecedor,
  // para evitar validação de "exatamente 7 dígitos" do backend em strings vazias
  if (!item.value.isFornecedor && !payload.codigoIbge) {
    payload.codigoIbge = null
  }

  const isNovo = !isEditing.value
  const idAnterior = item.value.idParceiro

  const sucesso = await submitForm('Parceiros', 'idParceiro', payload)

  if (sucesso && item.value.idParceiro && (isNovo || idAnterior === item.value.idParceiro)) {
    // --- Sincroniza Modais Transportadora via endpoint dedicado (diff) ---
    // O endpoint PUT /Parceiros/{id}/ModaisTransportadora faz diff: adiciona os novos, remove os ausentes
    try {
      await api.put(`/Parceiros/${item.value.idParceiro}/ModaisTransportadora`, modaisSelecionados)
    } catch (error) {
      console.warn('Erro ao sincronizar modais transportadora:', error)
    }

    // Salva dados do vendedor se necessário
    await salvarVendedorSeNecessario()

    const acao = isNovo ? 'cadastrado' : 'atualizado'
    emit('show-notification', {
      message: `Parceiro ${item.value.nomeFantasia} (ID: ${item.value.idParceiro}) ${acao} com sucesso!`,
      type: 'success'
    })
  }
}

// --- Exclusão de Endereço via API ---
async function excluirEndereco (end, idx) {
  try {
    if (isParceiroSaved.value && end.idParceiroEndereco) {
      await api.delete(`/Parceiros/${item.value.idParceiro}/Enderecos/${end.idParceiroEndereco}`)
    }
    item.value.enderecos.splice(idx, 1)
    normalizarEnderecosPrincipal()
  } catch (error) {
    let errorMsg = 'Erro ao excluir endereço.'
    if (error.response?.data && typeof error.response.data === 'string') {
      errorMsg = error.response.data
    } else if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail
    } else if (error.response?.data?.message) {
      errorMsg = error.response.data.message
    } else if (error.response?.data?.title) {
      errorMsg = error.response.data.title
    }
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

// --- Exclusão de Conta Bancária via API ---
async function excluirContaBancaria (cta, idx) {
  try {
    if (isParceiroSaved.value && cta.idParceiroConta) {
      await api.delete(`/Parceiros/${item.value.idParceiro}/ContasBancarias/${cta.idParceiroConta}`)
    }
    item.value.contasBancarias.splice(idx, 1)
  } catch (error) {
    let errorMsg = 'Erro ao excluir conta bancária.'
    if (error.response?.data && typeof error.response.data === 'string') {
      errorMsg = error.response.data
    } else if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail
    } else if (error.response?.data?.message) {
      errorMsg = error.response.data.message
    } else if (error.response?.data?.title) {
      errorMsg = error.response.data.title
    }
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

function toggleAccordion (key) {
  accordionState[key] = !accordionState[key]
}

// ============================================================
// CLAUSULAS DO FORNECEDOR (ParceiroClausula)
// ============================================================
const clausulasFornecedor = ref([])
const isLoadingClausulasFornecedor = ref(false)
const mostrarModalClausulaFornecedor = ref(false)
const clausulaFornecedorEditando = ref(null)
const isSavingClausulaFornecedor = ref(false)
const formClausulaFornecedor = reactive({
  titulo: '',
  texto: '',
  ordemExibicao: null,
  isDefinitiva: false,
  isFixa: false,
  lstStatusClausula: '',
  lstAplicacaoClausula: '',
  lstVisibilidade: '',
  lstProcessoInterno: ''
})

const getTextoListaClausula = (idItem, listaRef) => {
  if (!idItem || !listaRef) return '-'
  const obj = listaRef.find(i => String(i.idListaClienteItem) === String(idItem) || String(i.idListaSistemaItem) === String(idItem))
  return obj ? (obj.textoLista || obj.descricaoBreve) : idItem
}

const carregarClausulasFornecedor = async () => {
  if (!item.value.idParceiro) return
  isLoadingClausulasFornecedor.value = true
  try {
    const { data } = await api.get(`/ParceiroClausulas?IdParceiro=${item.value.idParceiro}&PageSize=100`)
    clausulasFornecedor.value = data.items || data || []
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao carregar cláusulas.'

    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }

    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingClausulasFornecedor.value = false
  }
}

const abrirModalClausulaFornecedor = () => {
  clausulaFornecedorEditando.value = null
  Object.assign(formClausulaFornecedor, {
    titulo: '',
    texto: '',
    ordemExibicao: null,
    isDefinitiva: false,
    isFixa: false,
    lstStatusClausula: '',
    lstAplicacaoClausula: '',
    lstVisibilidade: '',
    lstProcessoInterno: ''
  })
  mostrarModalClausulaFornecedor.value = true
}

const editarClausulaFornecedor = (row) => {
  clausulaFornecedorEditando.value = row
  Object.assign(formClausulaFornecedor, {
    titulo: row.titulo,
    texto: row.texto,
    ordemExibicao: row.ordemExibicao,
    isDefinitiva: row.isDefinitiva,
    isFixa: row.isFixa,
    lstStatusClausula: row.lstStatusClausula,
    lstAplicacaoClausula: row.lstAplicacaoClausula,
    lstVisibilidade: row.lstVisibilidade,
    lstProcessoInterno: row.lstProcessoInterno
  })
  mostrarModalClausulaFornecedor.value = true
}

const salvarClausulaFornecedor = async () => {
  if (!formClausulaFornecedor.titulo || !formClausulaFornecedor.texto) {
    emit('show-notification', { message: 'Preencha o título e o texto da cláusula.', type: 'warning' })
    return
  }
  isSavingClausulaFornecedor.value = true
  try {
    if (clausulaFornecedorEditando.value) {
      await api.put(`/ParceiroClausulas/${clausulaFornecedorEditando.value.idParceiroClausula}`, {
        titulo: formClausulaFornecedor.titulo,
        texto: formClausulaFornecedor.texto,
        isDefinitiva: formClausulaFornecedor.isDefinitiva,
        isFixa: formClausulaFornecedor.isFixa,
        lstStatusClausula: formClausulaFornecedor.lstStatusClausula,
        lstAplicacaoClausula: formClausulaFornecedor.lstAplicacaoClausula || null,
        lstVisibilidade: formClausulaFornecedor.lstVisibilidade || null,
        lstProcessoInterno: formClausulaFornecedor.lstProcessoInterno || null,
        xmin: clausulaFornecedorEditando.value.xmin
      })
      emit('show-notification', { message: 'Cláusula atualizada.', type: 'success' })
    } else {
      await api.post('/ParceiroClausulas', {
        idParceiro: item.value.idParceiro,
        titulo: formClausulaFornecedor.titulo,
        texto: formClausulaFornecedor.texto,
        isDefinitiva: formClausulaFornecedor.isDefinitiva,
        isFixa: formClausulaFornecedor.isFixa,
        lstAplicacaoClausula: formClausulaFornecedor.lstAplicacaoClausula || null,
        lstVisibilidade: formClausulaFornecedor.lstVisibilidade || null,
        lstProcessoInterno: formClausulaFornecedor.lstProcessoInterno || null
      })
      emit('show-notification', { message: 'Cláusula criada.', type: 'success' })
    }
    mostrarModalClausulaFornecedor.value = false
    await carregarClausulasFornecedor()
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar cláusula.'

    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }

    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingClausulaFornecedor.value = false
  }
}

const toggleStatusClausulaFornecedor = async (row) => {
  const novoStatus = row.lstStatusClausula === 'ST1' ? 'ST2' : 'ST1'
  try {
    await api.patch(`/ParceiroClausulas/${row.idParceiroClausula}/status`, {
      lstStatusClausula: novoStatus
    })
    emit('show-notification', {
      message: `Cláusula ${novoStatus === 'ST1' ? 'ativada' : 'inativada'}.`,
      type: 'success'
    })
    await carregarClausulasFornecedor()
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao alterar status.'

    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }

    emit('show-notification', { message: msg, type: 'error' })
  }
}

const excluirClausulaFornecedor = async (row) => {
  try {
    await api.delete(`/ParceiroClausulas/${row.idParceiroClausula}`)
    emit('show-notification', { message: 'Cláusula excluída.', type: 'success' })
    await carregarClausulasFornecedor()
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao excluir cláusula.'

    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }

    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ============================================================
// DADOS DO VENDEDOR (ParceiroVendedor)
// ============================================================
const vendedorData = ref({})
const isLoadingVendedor = ref(false)

const carregarVendedorData = async () => {
  if (!item.value.idParceiro || !item.value.isVendedor) return
  isLoadingVendedor.value = true
  try {
    const { data } = await api.get(`/ParceiroVendedores/${item.value.idParceiro}`)
    vendedorData.value = data
  } catch (error) {
    if (error.response?.status !== 404) {
      console.warn('Erro ao carregar dados do vendedor:', error)
    }
    // 404 = ainda não cadastrado, mantém objeto vazio
    if (error.response?.status !== 404) {
      vendedorData.value = {}
    }
  } finally {
    isLoadingVendedor.value = false
  }
}

// Helper para salvar dados do vendedor junto com o parceiro
const salvarVendedorSeNecessario = async (emitirNotificacao = false) => {
  if (!item.value.idParceiro || !item.value.isVendedor) return true
  if (!vendedorData.value || Object.keys(vendedorData.value).length === 0) return true

  try {
    // Verifica se já existe (tem idParceiro preenchido)
    const exists = vendedorData.value.idParceiro && vendedorData.value.idParceiro > 0
    const payload = { ...vendedorData.value, idParceiro: item.value.idParceiro }

    if (exists) {
      await api.put(`/ParceiroVendedores/${item.value.idParceiro}`, payload)
    } else {
      await api.post(`/ParceiroVendedores/${item.value.idParceiro}`, payload)
    }

    // Atualiza vendedorData com o que veio do backend
    if (!exists) {
      vendedorData.value.idParceiro = item.value.idParceiro
    }

    if (emitirNotificacao) {
      emit('show-notification', { message: 'Dados do vendedor salvos com sucesso.', type: 'success' })
    }
    return true
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar dados do vendedor.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    if (emitirNotificacao) {
      emit('show-notification', { message: msg, type: 'error' })
    }
    return false
  }
}

// Quando o parceiro for salvo, carregar dados relacionados
watch(() => item.value.idParceiro, (id) => {
  if (id) {
    carregarClausulasFornecedor()
    if (item.value.isVendedor) carregarVendedorData()
    if (item.value.isCliente) carregarPrecosCliente()
  }
})

// Quando ativar o switch vendedor, prepara objeto vazio
watch(() => item.value.isVendedor, (val) => {
  if (val && (!vendedorData.value || !vendedorData.value.idParceiro)) {
    vendedorData.value = {
      idParceiro: item.value.idParceiro || 0,
      idUsuario: null,
      idParceiroGestorComercial: null,
      percentualComissaoComoVendedor: 0,
      percentualComissaoComoGestor: 0,
      lstPapelComercialPadrao: null,
      regiaoAtuacao: ''
    }
  } else if (!val) {
    vendedorData.value = {}
  }
})

// ============================================================
// TABELAS DE PREÇO VINCULADAS AO CLIENTE (ParceiroClientePrecoComercial)
// ============================================================
const precosCliente = ref([])
const precoClienteEmEdicao = ref(null)

const showModalPrecoCliente = computed({
  get: () => !!precoClienteEmEdicao.value,
  set: (val) => { if (!val) precoClienteEmEdicao.value = null }
})

const carregarPrecosCliente = async () => {
  if (!item.value.idParceiro || !item.value.isCliente) {
    precosCliente.value = []
    return
  }
  try {
    const { data } = await api.get(`/ParceirosClientesPrecosComerciais?idParceiro=${item.value.idParceiro}`)
    precosCliente.value = Array.isArray(data) ? data : []
  } catch (error) {
    console.warn('Erro ao carregar tabelas de preço do cliente:', error)
    precosCliente.value = []
  }
}

const abrirModalPrecoCliente = () => {
  if (!isParceiroSaved.value) return
  precoClienteEmEdicao.value = {
    _isNew: true,
    idParceiroClientePrecoComercial: 0,
    idPrecoComercial: null,
    tsInicioVigencia: null,
    tsFimVigencia: null
  }
}

const editarPrecoCliente = (preco) => {
  if (!isParceiroSaved.value) return
  precoClienteEmEdicao.value = { ...preco, _isNew: false }
}

const salvarPrecoClienteLocal = async () => {
  const preco = precoClienteEmEdicao.value
  if (!preco) return

  if (!preco.idPrecoComercial) {
    emit('show-notification', { message: 'Selecione a Tabela de Preços.', type: 'warning' })
    return
  }

  const payload = {
    idPrecoComercial: preco.idPrecoComercial,
    tsInicioVigencia: preco.tsInicioVigencia || null,
    tsFimVigencia: preco.tsFimVigencia || null
  }

  try {
    if (preco._isNew) {
      await api.post(`/ParceirosClientesPrecosComerciais?idParceiro=${item.value.idParceiro}`, payload)
    } else {
      await api.put(`/ParceirosClientesPrecosComerciais/${preco.idParceiroClientePrecoComercial}`, payload)
    }
    precoClienteEmEdicao.value = null
    await carregarPrecosCliente()
    emit('show-notification', { message: 'Tabela de preços vinculada com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar vínculo.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const excluirPrecoCliente = async (preco) => {
  if (!isParceiroSaved.value) return
  try {
    await api.delete(`/ParceirosClientesPrecosComerciais/${preco.idParceiroClientePrecoComercial}`)
    await carregarPrecosCliente()
    emit('show-notification', { message: 'Vínculo removido com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao remover vínculo.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

watch(() => item.value.isCliente, (val) => {
  if (val && item.value.idParceiro) carregarPrecosCliente()
  else if (!val) precosCliente.value = []
})
</script>

<template>
  <FormularioCadastro
    module-name="Parceiro de Negócio"
    :is-loading="isSaving"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :accordion-state="accordionState"
    :status="getStatusTexto(item.lstStatus)"
    :ocultar-salvar="!isParceiroSaved"
    :desabilitar-salvar-alteracoes="isColecoesBlocked"
    @submit="salvarParceiro"
    @submit-continuar="salvarAlteracoesParceiro"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
  >
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="abrirAuditoria"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <!-- FRAME 1: DADOS BÁSICOS -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('basico')">
        <h5 class="mb-0"><i class="bi bi-person-lines-fill me-2"></i>Dados Básicos</h5>
        <i class="bi" :class="accordionState.basico ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>

      <div v-show="accordionState.basico" class="card-body">
        <!-- Bloco: Identificação -->
        <h6 class="text-muted text-uppercase small fw-bold mb-3 border-bottom pb-1 border-secondary-subtle">Identificação</h6>
        <div class="row g-3 mb-4">
          <div class="col-md-3">
            <CampoInput id="apelido" label="Apelido (Interno)" v-model="item.apelido" />
          </div>
          <div class="col-md-3">
            <!-- GUARDIÃO: ID corrigido -->
            <CampoSelect id="lstTipoPessoa" label="Tipo Pessoa" v-model="item.lstTipoPessoa" :options="listas.tiposPessoa" option-value="idListaClienteItem" option-label="textoLista" required />
          </div>
          <div class="col-md-3">
            <!-- GUARDIÃO: ID corrigido -->
            <CampoInput id="documento" label="Documento (CPF / CNPJ / Tax ID):" v-model="item.documento" required />
          </div>
          <div class="col-md-3">
            <CampoBuscaLista id="lstTipoDocumento" label="Tipo de Documento" v-model="item.lstTipoDocumento" tabela="Parceiro" />
          </div>
          <div class="col-md-6">
            <!-- GUARDIÃO: ID corrigido -->
            <CampoInput id="nomeFantasia" label="Nome Fantasia" v-model="item.nomeFantasia" required />
          </div>
          <div class="col-md-6">
            <!-- GUARDIÃO: ID corrigido -->
            <CampoInput id="razaoSocial" label="Razão Social" v-model="item.razaoSocial" required />
          </div>
        </div>

        <h6 class="text-muted text-uppercase small fw-bold mb-3 border-bottom pb-1 border-secondary-subtle">Grupo Empresa</h6>
        <div class="row g-3 mb-4">
          <div class="col-md-2">
            <!-- GUARDIÃO: ID corrigido -->
            <CampoSelect
              id="isGrupoEmpresa"
              label="É Grupo Empresa?"
              v-model="item.isGrupoEmpresa"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              required
            />
          </div>
          <div class="col-md-4">
            <!-- GUARDIÃO: ID corrigido -->
            <CampoBusca
              id="idGrupoEmpresa"
              label="Vincular a Grupo"
              v-model="item.idGrupoEmpresa"
              :configuracao-busca="confBuscaGrupo"
              :disabled="item.isGrupoEmpresa === true"
            />
          </div>
        </div>

        <h6 class="text-muted text-uppercase small fw-bold mb-3 border-bottom pb-1 border-secondary-subtle">Classificação e Impostos</h6>
        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <!-- GUARDIÃO: ID corrigido -->
            <CampoSelect id="lstRegimeTributario" label="Regime Tributário" v-model="item.lstRegimeTributario" :options="listas.regimes" option-value="idListaClienteItem" option-label="textoLista" />
          </div>
          <div class="col-md-4"><CampoInput id="inscricaoEstadual" label="Inscrição Estadual" v-model="item.inscricaoEstadual" /></div>
          <div class="col-md-4"><CampoInput id="inscricaoMunicipal" label="Inscrição Municipal" v-model="item.inscricaoMunicipal" /></div>
        </div>

        <h6 class="text-muted text-uppercase small fw-bold mb-3 border-bottom pb-1 border-secondary-subtle">Contatos Principais</h6>
        <div class="row g-3">
          <div class="col-md-4"><CampoInput id="emailPrincipal" label="E-mail Principal" v-model="item.emailPrincipal" /></div>
          <div class="col-md-3"><CampoInput id="telCelular" label="Celular / WhatsApp" v-model="item.telCelular" /></div>
          <div class="col-md-3"><CampoInput id="telFixo" label="Telefone Fixo" v-model="item.telFixo" /></div>
          <div class="col-md-2"><CampoInput id="site" label="Website" v-model="item.site" /></div>
        </div>

        <div class="border-top border-secondary-subtle pt-3 mt-4">
          <div class="row g-3">
            <div class="col-md-3">
              <!-- GUARDIÃO: ID corrigido -->
              <CampoSelect id="lstStatus" label="Status do Parceiro" v-model="item.lstStatus" :options="listas.status" option-value="idListaClienteItem" option-label="textoLista" required />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Botão Salvar Parceiro e Liberar Coleções (apenas criação) -->
    <div class="mb-4" v-if="!isParceiroSaved">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarParceiroLocalmente"
        :disabled="isSavingParceiroLocal || !item.nomeFantasia || !item.documento || !item.lstTipoPessoa || !item.lstStatus"
      >
        <span v-if="isSavingParceiroLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Parceiro e Liberar Endereços/Contas
      </button>
    </div>

    <!-- FRAME: ENDEREÇOS -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('enderecos')">
        <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Endereços</h5>
        <i class="bi" :class="accordionState.enderecos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.enderecos" class="card-body border-top border-secondary-subtle p-0" :class="{ 'pe-none opacity-50': isColecoesBlocked }">
        <div class="admin-table-container border-0 shadow-none mb-0">
          <table class="table admin-table m-0">
            <thead>
              <tr>
                <th style="width: 100px;">Principal</th>
                <th style="width: 20%;">Descrição</th>
                <th style="width: 15%;">País</th>
                <th>Logradouro/Número</th>
                <th style="width: 20%;">Cidade/UF</th>
                <th style="width: 120px;">Status</th>
                <th class="text-end" style="width: 100px;">
                  <button type="button" class="btn btn-sm btn-primary py-0" @click.stop="iniciarNovoEndereco" :disabled="isColecoesBlocked"><i class="bi bi-plus-lg"></i></button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(end, idx) in item.enderecos" :key="idx">
                <td class="text-center">
                  <span v-if="end.isPrincipal" class="badge badge-ghost-success" title="Endereço Principal">
                    <i class="bi bi-check-lg"></i>
                  </span>
                </td>
                <td class="fw-bold">{{ end.descricao }}</td>
                <td>{{ end.pais }}</td>
                <td>{{ end.logradouro }}, {{ end.numero }} <span v-if="end.complemento"> - {{ end.complemento }}</span></td>
                <td>{{ end.cidade }}/{{ end.uf }}</td>
                <td>
                  <span class="badge" :class="obterClasseStatusGhost(getStatusTexto(end.lstStatus))">
                    {{ getStatusTexto(end.lstStatus) }}
                  </span>
                </td>
                <td class="acoes-cell text-end pe-3">
                  <button type="button" class="btn-icon" @click="editarEndereco(end, idx)"><i class="bi bi-pencil-fill"></i></button>
                  <button type="button" class="btn-icon text-danger" @click="excluirEndereco(end, idx)"><i class="bi bi-trash-fill"></i></button>
                </td>
              </tr>
              <tr v-if="item.enderecos.length === 0"><td colspan="7" class="text-center py-4 text-muted">Nenhum endereço registado.</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- FRAME: CONTAS BANCÁRIAS -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('contas')">
        <h5 class="mb-0"><i class="bi bi-bank me-2"></i>Contas Bancárias</h5>
        <i class="bi" :class="accordionState.contas ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.contas" class="card-body border-top border-secondary-subtle p-0" :class="{ 'pe-none opacity-50': isColecoesBlocked }">
        <div class="admin-table-container border-0 shadow-none mb-0">
          <table class="table admin-table m-0">
            <thead>
              <tr>
                <th style="width: 60px;">Padrão</th>
                <th style="width: 20%;">Descrição / Apelido</th>
                <th style="width: 25%;">Instituição Financeira</th>
                <th>Agência / Conta</th>
                <th style="width: 100px;">Moeda</th>
                <th style="width: 120px;">Status</th>
                <th class="text-end" style="width: 100px;">
                  <button type="button" class="btn btn-sm btn-primary py-0" @click.stop="iniciarNovaConta" :disabled="isColecoesBlocked"><i class="bi bi-plus-lg"></i></button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cta, idx) in item.contasBancarias" :key="idx">
                <td class="align-middle text-center">
                  <span v-if="cta.isPadraoNaMoeda" class="badge badge-ghost-success" title="Conta Padrão nesta Moeda">
                    <i class="bi bi-check-lg"></i>
                  </span>
                </td>
                <td class="align-middle fw-semibold">{{ cta.descricao || '-' }}</td>
                <td class="align-middle">
                  <span v-if="cta.nomeBanco" class="d-block">{{ cta.nomeBanco }}</span>
                  <span v-else class="d-block text-muted">Banco ID: {{ cta.idBanco }}</span>
                </td>
                <td class="align-middle">{{ cta.agencia }} / {{ cta.numeroConta }}</td>
                <td class="align-middle">{{ getMoedaTexto(cta.lstMoeda) }}</td>
                <td class="align-middle">
                  <span class="badge" :class="obterClasseStatusGhost(getStatusTexto(cta.lstStatus))">
                    {{ getStatusTexto(cta.lstStatus) }}
                  </span>
                </td>
                <td class="acoes-cell text-end pe-3 align-middle">
                  <button type="button" class="btn-icon" @click="editarConta(cta, idx)"><i class="bi bi-pencil-fill"></i></button>
                  <button type="button" class="btn-icon text-danger" @click="excluirContaBancaria(cta, idx)"><i class="bi bi-trash-fill"></i></button>
                </td>
              </tr>
              <tr v-if="item.contasBancarias.length === 0"><td colspan="7" class="text-center py-4 text-muted">Nenhuma conta bancária registada.</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- FRAME: CLÁUSULAS DO FORNECEDOR -->
    <div :class="{ 'pe-none opacity-50': isColecoesBlocked }">
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('clausulas')">
          <h5 class="mb-0"><i class="bi bi-file-text me-2"></i>Cláusulas do Fornecedor</h5>
          <i class="bi" :class="accordionState.clausulas ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.clausulas" class="card-body border-top border-secondary-subtle p-0">
          <div class="admin-table-container border-0 shadow-none mb-0">
            <table class="table admin-table m-0">
              <thead>
                <tr>
                  <th style="width: 60px;">Ordem</th>
                  <th style="width: 200px;">Título</th>
                  <th style="width: 80px;">Definitiva</th>
                  <th style="width: 60px;">Fixa</th>
                  <th style="width: 100px;">Aplicação</th>
                  <th style="width: 100px;">Visibilidade</th>
                  <th style="width: 80px;">Status</th>
                  <th class="text-end" style="width: 120px;">
                    <button type="button" class="btn btn-sm btn-primary py-0" @click.stop="abrirModalClausulaFornecedor" :disabled="isColecoesBlocked || !item.idParceiro">
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(cl, idx) in clausulasFornecedor" :key="cl.idParceiroClausula || idx">
                  <td class="text-center">{{ cl.ordemExibicao }}</td>
                  <td class="fw-bold">{{ cl.titulo }}</td>
                  <td class="text-center">
                    <i :class="cl.isDefinitiva ? 'bi bi-check-circle-fill text-success' : 'bi bi-x-circle-fill text-secondary'"></i>
                  </td>
                  <td class="text-center">
                    <i :class="cl.isFixa ? 'bi bi-check-circle-fill text-success' : 'bi bi-x-circle-fill text-secondary'"></i>
                  </td>
                  <td>{{ getTextoListaClausula(cl.lstAplicacaoClausula, listas.lstAplicacaoClausula) }}</td>
                  <td>{{ getTextoListaClausula(cl.lstVisibilidade, listas.lstVisibilidade) }}</td>
                  <td>
                    <span class="badge" :class="cl.lstStatusClausula === 'ST1' ? 'bg-success' : 'bg-secondary'">
                      {{ cl.lstStatusClausula === 'ST1' ? 'Ativo' : 'Inativo' }}
                    </span>
                  </td>
                  <td class="acoes-cell text-end pe-3">
                    <button type="button" class="btn-icon" @click="editarClausulaFornecedor(cl)" title="Editar">
                      <i class="bi bi-pencil-fill"></i>
                    </button>
                    <button type="button" class="btn-icon" :class="cl.lstStatusClausula === 'ST1' ? 'text-warning' : 'text-success'" @click="toggleStatusClausulaFornecedor(cl)" :title="cl.lstStatusClausula === 'ST1' ? 'Inativar' : 'Reativar'">
                      <i class="bi" :class="cl.lstStatusClausula === 'ST1' ? 'bi-pause-circle' : 'bi-play-circle'"></i>
                    </button>
                    <button type="button" class="btn-icon text-danger" @click="excluirClausulaFornecedor(cl)" title="Excluir">
                      <i class="bi bi-trash-fill"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="clausulasFornecedor.length === 0">
                  <td colspan="8" class="text-center py-4 text-muted">Nenhuma cláusula cadastrada para este fornecedor.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- FUNÇÃO CLIENTE -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('cliente')">
        <div class="d-flex align-items-center">
           <div class="form-check form-switch mb-0 me-3 d-flex align-items-center">
             <input class="form-check-input switch-lg" type="checkbox" id="chkCli" v-model="item.isCliente" @click.stop :disabled="isColecoesBlocked">
           </div>
           <h5 class="mb-0"><i class="bi bi-person-check me-2"></i>Função Cliente</h5>
        </div>
        <i class="bi" :class="accordionState.cliente ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.cliente" class="card-body border-top border-secondary-subtle">
        <fieldset :disabled="!item.isCliente">
          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <!-- GUARDIÃO: IDs Corrigidos para match perfeito -->
              <CampoBusca id="idVendedor" label="Vendedor Responsável" v-model="item.idVendedor" :configuracao-busca="confBuscaVendedor" />
            </div>
            <div class="col-md-4">
              <CampoBuscaLista id="lstSegmento" label="Segmento" v-model="item.lstSegmento" tabela="Parceiro" :required="item.isCliente" />
            </div>
            <div class="col-md-4">
              <CampoBuscaLista id="lstRamoAtividade" label="Ramo de Atividade" v-model="item.lstRamoAtividade" tabela="Parceiro" :required="item.isCliente" />
            </div>
          </div>
          <div class="row g-3">
            <div class="col-md-4"><CampoInput id="suframa" label="Código SUFRAMA" v-model="item.suframa" /></div>
          </div>
          <h6 class="text-muted text-uppercase small fw-bold mb-3 mt-3 border-bottom pb-1 border-secondary-subtle">Endereços de Negócio (Cliente)</h6>
          <div class="row g-3">
            <div class="col-md-4">
              <CampoBusca id="idEnderecoClienteFaturamento" label="Endereço de Faturamento" v-model="item.idEnderecoClienteFaturamento" :configuracao-busca="confBuscaEnderecoParceiro" :required="item.isCliente" />
            </div>
            <div class="col-md-4">
              <CampoBusca id="idEnderecoClienteEntrega" label="Endereço de Entrega" v-model="item.idEnderecoClienteEntrega" :configuracao-busca="confBuscaEnderecoParceiro" :required="item.isCliente" />
            </div>
            <div class="col-md-4">
              <CampoBusca id="idEnderecoClienteCobranca" label="Endereço de Cobrança" v-model="item.idEnderecoClienteCobranca" :configuracao-busca="confBuscaEnderecoParceiro" :required="item.isCliente" />
            </div>
          </div>
          <!-- Sub-frame: Atributos do Cliente -->
          <div v-if="item.isCliente && listasVinculadasCliente.length > 0" class="mt-4 border-top border-secondary-subtle pt-3">
            <h6 class="text-muted text-uppercase small fw-bold mb-3">
              <i class="bi bi-list-check me-2"></i> Atributos do Cliente
            </h6>
            <div class="row g-3">
              <div class="col-md-4" v-for="lista in listasVinculadasCliente" :key="lista.prefixoItemLista">
                <CampoBuscaLista
                  :id="'attr_cli_'+lista.prefixoItemLista"
                  :label="lista.nomeLista"
                  v-model="respostasAtributosCliente[lista.prefixoItemLista]"
                  :prefixo="lista.prefixoItemLista"
                  :required="item.isCliente"
                />
              </div>
            </div>
          </div>
          <!-- Sub-frame: Tabelas de Preço Vinculadas -->
          <div v-if="item.isCliente" class="mt-4 border-top border-secondary-subtle pt-3">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="text-muted text-uppercase small fw-bold mb-0">
                <i class="bi bi-tags me-2"></i> Tabelas de Preço Vinculadas
              </h6>
              <button type="button" class="btn btn-sm btn-primary py-0" @click="abrirModalPrecoCliente" :disabled="!isParceiroSaved">
                <i class="bi bi-plus-lg"></i> Adicionar
              </button>
            </div>
            <div class="admin-table-container border-0 shadow-none mb-0">
              <table class="table table-sm table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Tabela de Preços</th>
                    <th style="width: 170px;">Início Vigência</th>
                    <th style="width: 170px;">Fim Vigência</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="precosCliente.length === 0">
                    <td colspan="4" class="text-center text-muted py-3">Nenhuma tabela de preços vinculada.</td>
                  </tr>
                  <tr v-for="preco in precosCliente" :key="preco.idParceiroClientePrecoComercial">
                    <td class="fw-medium">{{ preco.nomePrecoComercial || ('#' + preco.idPrecoComercial) }}</td>
                    <td>{{ preco.tsInicioVigencia ? formatarTimestampBR(preco.tsInicioVigencia) : '—' }}</td>
                    <td>{{ preco.tsFimVigencia ? formatarTimestampBR(preco.tsFimVigencia) : '—' }}</td>
                    <td class="text-end">
                      <button type="button" class="btn btn-sm btn-outline-secondary py-0 me-1" @click="editarPrecoCliente(preco)" :disabled="!isParceiroSaved"><i class="bi bi-pencil"></i></button>
                      <button type="button" class="btn btn-sm btn-outline-danger py-0" @click="excluirPrecoCliente(preco)" :disabled="!isParceiroSaved"><i class="bi bi-trash"></i></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </fieldset>
      </div>
    </div>

    <!-- FUNÇÃO FORNECEDOR -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('fornecedor')">
        <div class="d-flex align-items-center">
           <div class="form-check form-switch mb-0 me-3 d-flex align-items-center">
             <input class="form-check-input switch-lg" type="checkbox" id="chkForn" v-model="item.isFornecedor" @click.stop :disabled="isColecoesBlocked">
           </div>
           <h5 class="mb-0"><i class="bi bi-box-seam me-2"></i>Função Fornecedor</h5>
        </div>
        <i class="bi" :class="accordionState.fornecedor ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.fornecedor" class="card-body border-top border-secondary-subtle">
        <fieldset :disabled="!item.isFornecedor">
          <div class="row g-3">
            <div class="col-md-6">
              <!-- GUARDIÃO: ID Corrigido -->
              <CampoSelect id="lstCategoria" label="Categoria do Fornecedor" v-model="item.lstCategoria" :options="listas.categorias" option-value="idListaClienteItem" option-label="textoLista" :required="item.isFornecedor" />
            </div>

            <!-- GUARDIÃO: NOVO CAMPO IBGE SEFAZ -->
            <div class="col-md-4">
              <CampoInput
                id="codigoIbge"
                label="Código IBGE do Município"
                v-model="item.codigoIbge"
                type="text"
                maxlength="7"
                :required="isIbgeObrigatorio"
                :disabled="!podeEditar || isEstrangeiro"
                placeholder="Apenas 7 dígitos..."
              />
              <small v-if="isEstrangeiro" class="text-info mt-1 d-block fw-medium">
                <i class="bi bi-info-circle"></i> Regra SEFAZ: Preenchimento automático para Estrangeiros.
              </small>
            </div>
          </div>
          <h6 class="text-muted text-uppercase small fw-bold mb-3 mt-3 border-bottom pb-1 border-secondary-subtle">Endereços de Negócio (Fornecedor)</h6>
          <div class="row g-3">
            <div class="col-md-6">
              <CampoBusca id="idEnderecoFornecedorFaturamento" label="Endereço de Faturamento" v-model="item.idEnderecoFornecedorFaturamento" :configuracao-busca="confBuscaEnderecoParceiro" :required="item.isFornecedor" />
            </div>
            <div class="col-md-6">
              <CampoBusca id="idEnderecoFornecedorExpedicao" label="Endereço de Expedição" v-model="item.idEnderecoFornecedorExpedicao" :configuracao-busca="confBuscaEnderecoParceiro" :required="item.isFornecedor" />
            </div>
          </div>
        </fieldset>
      </div>
    </div>

    <!-- FUNÇÃO TRANSPORTADORA -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('transportadora')">
        <div class="d-flex align-items-center">
           <div class="form-check form-switch mb-0 me-3 d-flex align-items-center">
             <input class="form-check-input switch-lg" type="checkbox" id="chkTrans" v-model="item.isTransportadora" @click.stop :disabled="isColecoesBlocked">
           </div>
           <h5 class="mb-0"><i class="bi bi-truck me-2"></i>Função Transportadora</h5>
        </div>
        <i class="bi" :class="accordionState.transportadora ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.transportadora" class="card-body border-top border-secondary-subtle">
        <fieldset :disabled="!item.isTransportadora || isColecoesBlocked">
          <div class="row g-3 mb-3">
            <div class="col-md-4"><CampoInput id="rntrc" label="RNTRC" v-model="item.rntrc" /></div>
            <div class="col-md-4"><CampoInput id="codigoAntt" label="Código ANTT" v-model="item.codigoAntt" /></div>
          </div>
          <div class="row g-3">
            <div class="col-md-12">
              <label class="form-label">Modais Atendidos</label>
              <div class="border border-secondary-subtle rounded p-3 bg-body-tertiary">
                <div class="row row-cols-auto g-3">
                  <div class="col" v-for="modal in listas.modais" :key="modal.idListaClienteItem">
                    <div class="form-check">
                      <!-- Para arrays simples o ID não tem de ser exato ao v-model porque o Robô não os audita -->
                      <input class="form-check-input" type="checkbox" :value="modal.idListaClienteItem" :id="'m-'+modal.idListaClienteItem" v-model="item.modaisTransportadora">
                      <label class="form-check-label" :for="'m-'+modal.idListaClienteItem">{{ modal.textoLista }}</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <h6 class="text-muted text-uppercase small fw-bold mb-3 mt-3 border-bottom pb-1 border-secondary-subtle">Endereços de Negócio (Transportadora)</h6>
          <div class="row g-3">
            <div class="col-md-6">
              <CampoBusca id="idEnderecoTransportadoraOperacao" label="Endereço de Operação" v-model="item.idEnderecoTransportadoraOperacao" :configuracao-busca="confBuscaEnderecoParceiro" :required="item.isTransportadora" />
            </div>
            <div class="col-md-6">
              <CampoBusca id="idEnderecoTransportadoraCobranca" label="Endereço de Cobrança" v-model="item.idEnderecoTransportadoraCobranca" :configuracao-busca="confBuscaEnderecoParceiro" :required="item.isTransportadora" />
            </div>
          </div>
        </fieldset>
      </div>
    </div>

    <!-- FUNÇÃO FINANCEIRA -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('financeira')">
        <div class="d-flex align-items-center">
           <div class="form-check form-switch mb-0 me-3 d-flex align-items-center">
             <input class="form-check-input switch-lg" type="checkbox" id="chkFin" v-model="item.isInstFinanceira" @click.stop :disabled="isColecoesBlocked">
           </div>
           <h5 class="mb-0"><i class="bi bi-cash-coin me-2"></i>Função Inst. Financeira</h5>
        </div>
        <i class="bi" :class="accordionState.financeira ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.financeira" class="card-body border-top border-secondary-subtle">
        <fieldset :disabled="!item.isInstFinanceira">
          <div class="row g-3">
            <div class="col-md-3"><CampoInput id="codigoCompe" label="Código COMPE" v-model="item.codigoCompe" maxlength="3" /></div>
            <div class="col-md-3"><CampoInput id="swiftBic" label="SWIFT / BIC" v-model="item.swiftBic" /></div>
          </div>
        </fieldset>
      </div>
    </div>

    <!-- FUNÇÃO ÓRGÃO DE GOVERNO -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('governo')">
        <div class="d-flex align-items-center">
           <div class="form-check form-switch mb-0 me-3 d-flex align-items-center">
             <input class="form-check-input switch-lg" type="checkbox" id="chkGov" v-model="item.isOrgaoGoverno" @click.stop :disabled="isColecoesBlocked">
           </div>
           <h5 class="mb-0"><i class="bi bi-building-columns me-2"></i>Função Órgão de Governo</h5>
        </div>
        <i class="bi" :class="accordionState.governo ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.governo" class="card-body border-top border-secondary-subtle">
        <fieldset :disabled="!item.isOrgaoGoverno">
          <div class="row g-3">
            <div class="col-md-4">
              <CampoSelect
                id="lstEsferaGovernamental"
                label="Esfera Governamental"
                v-model="item.lstEsferaGovernamental"
                :options="listas.esferasGov"
                option-value="idListaClienteItem"
                option-label="textoLista"
                :required="item.isOrgaoGoverno"
              />
            </div>

            <!-- GUARDIÃO: Campo IBGE replicado e sincronizado automaticamente via v-model -->
            <div class="col-md-4">
              <CampoInput
                id="codigoIbgeGoverno"
                label="Código IBGE do Município"
                v-model="item.codigoIbge"
                type="text"
                maxlength="7"
                :required="isIbgeObrigatorio"
                :disabled="!podeEditar || isEstrangeiro"
                placeholder="Apenas 7 dígitos..."
              />
              <small v-if="isEstrangeiro" class="text-info mt-1 d-block fw-medium">
                <i class="bi bi-info-circle"></i> Regra SEFAZ: Preenchimento automático para Estrangeiros.
              </small>
            </div>
          </div>
        </fieldset>
      </div>
    </div>

    <!-- FUNÇÃO VENDEDOR -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('vendedor')">
        <div class="d-flex align-items-center">
           <div class="form-check form-switch mb-0 me-3 d-flex align-items-center">
             <input class="form-check-input switch-lg" type="checkbox" id="chkVend" v-model="item.isVendedor" @click.stop :disabled="isColecoesBlocked">
           </div>
           <h5 class="mb-0"><i class="bi bi-person-badge me-2"></i>Função Vendedor</h5>
        </div>
        <i class="bi" :class="accordionState.vendedor ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="accordionState.vendedor" class="card-body border-top border-secondary-subtle">
        <fieldset :disabled="!item.isVendedor || !isParceiroSaved">
          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <CampoBusca
                id="idUsuario"
                label="Usuário do Sistema (Login)"
                v-model="vendedorData.idUsuario"
                :configuracao-busca="confBuscaUsuario"
                :disabled="!item.isVendedor"
              />
            </div>
            <div class="col-md-4">
              <CampoBuscaLista
                id="lstPapelComercialPadrao"
                label="Papel Comercial Padrão"
                v-model="vendedorData.lstPapelComercialPadrao"
                tabela="ParceiroVendedor"
                :disabled="!item.isVendedor"
              />
            </div>
            <div class="col-md-4">
              <CampoInput
                id="regiaoAtuacao"
                label="Região de Atuação"
                v-model="vendedorData.regiaoAtuacao"
                maxlength="100"
                :disabled="!item.isVendedor"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="percentualComissaoComoVendedor"
                label="% Comissão como Vendedor"
                v-model="vendedorData.percentualComissaoComoVendedor"
                type="percentual"
                :disabled="!item.isVendedor"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="percentualComissaoComoGestor"
                label="% Comissão como Gestor"
                v-model="vendedorData.percentualComissaoComoGestor"
                type="percentual"
                :disabled="!item.isVendedor"
              />
            </div>
          </div>
          <div class="row g-3">
            <div class="col-md-4">
              <CampoBusca
                id="idParceiroGestorComercial"
                label="Gestor Comercial"
                v-model="vendedorData.idParceiroGestorComercial"
                :configuracao-busca="confBuscaGestorComercial"
                :disabled="!item.isVendedor"
              />
            </div>
          </div>
        </fieldset>
      </div>
    </div>

    <!-- MODAL DE VÍNCULO DE TABELA DE PREÇOS -->
    <ModalFormulario v-model="showModalPrecoCliente" titulo="Vincular Tabela de Preços" @confirmar="salvarPrecoClienteLocal">
      <div v-if="precoClienteEmEdicao" class="row g-3">
        <div class="col-md-12">
          <CampoBusca id="idPrecoComercial" label="Tabela de Preços" v-model="precoClienteEmEdicao.idPrecoComercial" :configuracao-busca="confBuscaPrecoComercial" required />
        </div>
        <div class="col-md-6">
          <CampoTimeStamp id="tsInicioVigencia" label="Início da Vigência" v-model="precoClienteEmEdicao.tsInicioVigencia" nivel="minuto" />
        </div>
        <div class="col-md-6">
          <CampoTimeStamp id="tsFimVigencia" label="Fim da Vigência" v-model="precoClienteEmEdicao.tsFimVigencia" nivel="minuto" />
        </div>
      </div>
    </ModalFormulario>

    <!-- MODAL DE ENDEREÇO -->
    <ModalFormulario v-model="showModalEndereco" titulo="Manutenção de Endereço" @confirmar="salvarEnderecoLocal">
      <div v-if="enderecoEmEdicao" class="row g-3">
        <div class="col-md-12">
          <!-- IDs padronizados de forma independente, pois a validação ocorre na rotina salvarEnderecoLocal -->
          <CampoInput id="descricao" label="Descrição (Ex: Matriz, Depósito)" v-model="enderecoEmEdicao.descricao" required />
        </div>
        <div class="col-md-6">
          <CampoSelect id="pais" label="País" v-model="enderecoEmEdicao.pais" :options="listaPaises" required :disabled="!isEstrangeiro" />
        </div>
        <div class="col-md-6">
          <CampoInput id="codigoPais" label="Código do País (SPED - cPais)" v-model="enderecoEmEdicao.codigoPais" maxlength="4" placeholder="Ex: 1058" />
        </div>
        <template v-if="enderecoEmEdicao.pais === 'Brasil'">
          <div class="col-md-6">
            <label class="form-label">CEP</label>
            <input type="text" id="cep" class="form-control" v-model="enderecoEmEdicao.cep" maxlength="9" placeholder="00000-000" @input="formatarCep" @blur="buscarCep">
          </div>
        </template>
        <template v-else>
          <div class="col-md-6">
            <CampoInput id="cep" label="Código Postal (ZIP)" v-model="enderecoEmEdicao.cep" />
          </div>
        </template>
        <div class="col-md-6">
          <CampoInput id="logradouro" label="Logradouro" v-model="enderecoEmEdicao.logradouro" :disabled="enderecoEmEdicao.pais === 'Brasil'" required />
        </div>
        <div class="col-md-2">
          <CampoInput id="numero" label="Número" v-model="enderecoEmEdicao.numero" @blur="buscarCoordenadas" required />
        </div>
        <div class="col-md-4">
          <CampoInput id="complemento" label="Complemento" v-model="enderecoEmEdicao.complemento" />
        </div>
        <div class="col-md-6">
          <CampoInput id="bairro" label="Bairro" v-model="enderecoEmEdicao.bairro" :disabled="enderecoEmEdicao.pais === 'Brasil'" />
        </div>
        <div class="col-md-4">
          <CampoInput id="cidade" label="Cidade" v-model="enderecoEmEdicao.cidade" :disabled="enderecoEmEdicao.pais === 'Brasil'" required />
        </div>
        <div class="col-md-2">
          <CampoInput id="uf" label="UF" v-model="enderecoEmEdicao.uf" :disabled="enderecoEmEdicao.pais !== 'Brasil' || isEstrangeiro" required maxlength="2" />
        </div>

        <div class="col-md-12" v-if="enderecoEmEdicao.latitude || enderecoEmEdicao.longitude">
          <div class="p-2 border border-secondary-subtle rounded bg-body-tertiary d-flex gap-4">
             <span class="small text-body-secondary"><i class="bi bi-geo-alt-fill me-1"></i> Latitude: <strong class="text-body">{{ enderecoEmEdicao.latitude }}</strong></span>
             <span class="small text-body-secondary">Longitude: <strong class="text-body">{{ enderecoEmEdicao.longitude }}</strong></span>
          </div>
        </div>

        <div class="col-md-6">
          <CampoSelect id="isPrincipal" label="Endereço Principal?" v-model="enderecoEmEdicao.isPrincipal" :options="simNaoOptions" option-value="id" option-label="label" />
        </div>
        <div class="col-md-6">
          <CampoSelect id="lstStatus" label="Status do Endereço" v-model="enderecoEmEdicao.lstStatus" :options="listas.status" option-value="idListaClienteItem" option-label="textoLista" required />
        </div>
      </div>
    </ModalFormulario>

    <!-- MODAL DE CONTA BANCÁRIA -->
    <ModalFormulario v-model="showModalConta" titulo="Manutenção de Conta Bancária" tamanho="md" @confirmar="salvarContaLocal">
      <div v-if="contaEmEdicao" class="row g-3">
        <div class="col-md-6">
          <CampoInput id="descricao" label="Descrição (Ex: Conta Matriz, Dólar)" v-model="contaEmEdicao.descricao" />
        </div>
        <div class="col-md-6">
          <CampoBusca
            id="idBanco"
            label="Instituição Financeira"
            v-model="contaEmEdicao.idBanco"
            :configuracao-busca="confBuscaBanco"
            @selecionado="onBancoSelecionado"
            required
          />
        </div>
        <div class="col-md-3"><CampoInput id="agencia" label="Agência" v-model="contaEmEdicao.agencia" /></div>
        <div class="col-md-5"><CampoInput id="numeroConta" label="Número da Conta" v-model="contaEmEdicao.numeroConta" required /></div>
        <div class="col-md-4">
          <CampoSelect id="lstMoeda" label="Moeda" v-model="contaEmEdicao.lstMoeda" :options="listas.moedas" option-value="idListaClienteItem" option-label="textoLista" required />
        </div>
        <div class="col-md-4">
          <CampoSelect id="lstTipoChavePix" label="Tipo Chave Pix" v-model="contaEmEdicao.lstTipoChavePix" :options="listas.tiposPix" option-value="idListaClienteItem" option-label="textoLista" />
        </div>
        <div class="col-md-8">
          <CampoInput id="chavePix" label="Chave Pix" v-model="contaEmEdicao.chavePix" />
        </div>
        <div class="col-md-4">
          <CampoInput id="iban" label="IBAN" v-model="contaEmEdicao.iban" />
        </div>
        <div class="col-md-4">
          <CampoSelect id="lstRoutingNumberType" label="Routing Type" v-model="contaEmEdicao.lstRoutingNumberType" :options="listas.routingTypes" option-value="idListaClienteItem" option-label="textoLista" />
        </div>
        <div class="col-md-4">
          <CampoInput id="routingNumber" label="Routing Number" v-model="contaEmEdicao.routingNumber" />
        </div>
        <div class="col-md-6">
          <CampoSelect id="lstStatus" label="Status da Conta" v-model="contaEmEdicao.lstStatus" :options="listas.status" option-value="idListaClienteItem" option-label="textoLista" required />
        </div>
        <div class="col-md-6 d-flex align-items-end">
          <div class="form-check form-switch mb-3">
            <input class="form-check-input" type="checkbox" id="isPadraoNaMoeda" v-model="contaEmEdicao.isPadraoNaMoeda" />
            <label class="form-check-label fw-medium" for="isPadraoNaMoeda">
              <i class="bi bi-star-fill text-warning me-1"></i>Conta Padrão nesta Moeda
            </label>
          </div>
        </div>
      </div>
    </ModalFormulario>

    <!-- MODAL DE CLÁUSULA DO FORNECEDOR -->
    <ModalFormularioV2
      v-model="mostrarModalClausulaFornecedor"
      :titulo="clausulaFornecedorEditando ? 'Editar Cláusula do Fornecedor' : 'Nova Cláusula do Fornecedor'"
      :loading="isSavingClausulaFornecedor"
      tamanho="lg"
      @confirmar="salvarClausulaFornecedor"
    >
      <div class="row g-3">
        <div class="col-md-8">
          <CampoInput id="titulo" label="Título" v-model="formClausulaFornecedor.titulo" required maxlength="200" />
        </div>
        <div class="col-md-4">
          <CampoInput id="ordemExibicao" label="Ordem de Exibição" v-model="formClausulaFornecedor.ordemExibicao" type="decimal" />
        </div>
        <div class="col-12">
          <CampoInput id="texto" label="Texto da Cláusula" type="textarea" v-model="formClausulaFornecedor.texto" required />
        </div>
        <div class="col-md-4">
          <CampoSelect id="isDefinitiva" label="Cláusula Definitiva?"
            v-model="formClausulaFornecedor.isDefinitiva"
            :options="simNaoOptions"
            option-value="id"
            option-label="label" />
        </div>
        <div class="col-md-4">
          <CampoSelect id="isFixa" label="Cláusula Fixa?"
            v-model="formClausulaFornecedor.isFixa"
            :options="simNaoOptions"
            option-value="id"
            option-label="label" />
        </div>
        <div class="col-md-4">
          <CampoBuscaLista
            id="lstAplicacaoClausula"
            label="Aplicação (Módulo)"
            v-model="formClausulaFornecedor.lstAplicacaoClausula"
            tabela="ParceiroClausula"
            placeholder="Selecione a aplicação..." />
        </div>
        <div class="col-md-4">
          <CampoBuscaLista
            id="lstVisibilidade"
            label="Visibilidade"
            v-model="formClausulaFornecedor.lstVisibilidade"
            tabela="ParceiroClausula"
            placeholder="Selecione a visibilidade..." />
        </div>
        <div class="col-md-4">
          <CampoBuscaLista
            id="lstProcessoInterno"
            label="Processo Interno"
            v-model="formClausulaFornecedor.lstProcessoInterno"
            tabela="ParceiroClausula"
            placeholder="Selecione o processo..." />
        </div>
        <div class="col-md-4">
          <CampoSelect
            id="lstStatusClausula"
            label="Status"
            v-model="formClausulaFornecedor.lstStatusClausula"
            :options="listas.status"
            option-value="idListaClienteItem"
            option-label="textoLista"
            required />
        </div>
      </div>
    </ModalFormularioV2>

  </FormularioCadastro>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Parceiro"
    :chave-primaria="item.idParceiro"
    :titulo-registro="item.nomeFantasia || item.razaoSocial || item.apelido || 'Parceiro'"
    contexto="tenant"
    :tabelas-filhas="['ParceiroEndereco', 'ParceiroContaBancaria', 'ParceiroTransportadoraModal', 'ParceiroVendedor', 'ParceiroClausula']"
    chave-estrangeira="IdParceiro"
    :dicionarios="{
      'LstModalTransporte': listas.modais,
      'lstTipoPessoa': listas.tiposPessoa,
      'tipoPessoa': listas.tiposPessoa,
      'lstStatus': listas.status,
      'status': listas.status,
      'lstRegimeTributario': listas.regimes,
      'regimeTributario': listas.regimes,
      'lstSegmento': listas.segmentos,
      'segmento': listas.segmentos,
      'lstRamoAtividade': listas.ramos,
      'ramoAtividade': listas.ramos,
      'lstCategoria': listas.categorias,
      'categoria': listas.categorias,
      'lstMoeda': listas.moedas,
      'moeda': listas.moedas,
      'lstTipoChavePix': listas.tiposPix,
      'tipoChavePix': listas.tiposPix,
      'lstRoutingNumberType': listas.routingTypes,
      'routingNumberType': listas.routingTypes,
      'modaisTransportadora': listas.modais,
      'lstEsferaGovernamental': listas.esferasGov,
      'esferaGovernamental': listas.esferasGov,
      'lstTipoDocumento': listas.tiposDocumento,
      'tipoDocumento': listas.tiposDocumento
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
:deep(.form-label) { font-weight: 400 !important; }

.pointer { cursor: pointer; }

/* GUARDIÃO: O Switch Maior no Cabeçalho está de volta! */
.switch-lg { width: 2.5em; height: 1.25em; cursor: pointer; }

/* Grelhas Internas Adaptáveis */
.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.25rem;
  overflow-x: auto;
}
.admin-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--bs-border-color);
}
.admin-table td {
  border-bottom: 1px solid var(--bs-border-color);
  color: var(--bs-body-color);
}

:deep(.campo-input-container), :deep(.campo-select-container) { margin-bottom: 1rem; display: flex; flex-direction: column; }
.acoes-cell { text-align: right; vertical-align: middle; }
.btn-icon { background: none; border: none; color: #6c7d8e; padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: #212934; }
[data-bs-theme="dark"] .btn-icon:hover { color: #fff; }
</style>
